import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import { useTonAddress } from "@tonconnect/ui-react"

import { getTelegramStartParam, getUserIdFromToken } from "@/shared/api/auth"
import { apiClient } from "@/shared/api/client"

import { AnalyticsEvent } from "./events"
import {
  identifyAnalyticsTelegramUser,
  identifyAnalyticsUser,
  trackAnalyticsEvent,
} from "./track"
import { resolveScreenName } from "./screenNames"

/**
 * Prod-only analytics side effects: auth user id, session, SPA screen views, wallet connect.
 * Mount once inside the router (see App.tsx).
 */
export function GtmAnalytics() {
  const { pathname } = useLocation()
  const walletAddress = useTonAddress()
  const sessionTracked = useRef(false)
  const startParamTracked = useRef(false)
  const walletTracked = useRef<string | null>(null)
  const lastScreenPath = useRef<string | null>(null)

  useEffect(() => {
    identifyAnalyticsTelegramUser()
  }, [])

  useEffect(() => {
    if (startParamTracked.current) return
    const startParam = getTelegramStartParam()
    if (!startParam) return
    startParamTracked.current = true
    trackAnalyticsEvent(AnalyticsEvent.TelegramStartParam, {
      start_param: startParam,
    })
  }, [])

  useEffect(() => {
    if (sessionTracked.current) return
    sessionTracked.current = true

    void apiClient.ensureSession().then((ok) => {
      if (!ok) return
      identifyAnalyticsTelegramUser()
      identifyAnalyticsUser(getUserIdFromToken())
      trackAnalyticsEvent(AnalyticsEvent.SessionStart, {})
    })
  }, [])

  useEffect(() => {
    if (lastScreenPath.current === pathname) return
    lastScreenPath.current = pathname

    trackAnalyticsEvent(AnalyticsEvent.ScreenView, {
      page_path: pathname,
      screen_name: resolveScreenName(pathname),
    })
  }, [pathname])

  useEffect(() => {
    if (!walletAddress || walletTracked.current === walletAddress) return
    walletTracked.current = walletAddress
    trackAnalyticsEvent(AnalyticsEvent.WalletConnected, {
      wallet_address: walletAddress,
    })
  }, [walletAddress])

  return null
}
